import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Quote } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { api, mediaUrl, formatPrice } from "@/lib/api";
import { SmartLink, SectionHead, InfinityMark, Prose } from "@/components/common";
import { ResourceGrid } from "@/components/ResourceCard";
import { CommunityPromo, LeadMagnet } from "@/components/Promos";
import { SignupBar } from "@/components/EmailCapture";
import WeeklyCard from "@/components/WeeklyCard";
import { Users, ArrowUpRight } from "lucide-react";

function CommunityBar({ s }) {
  const { settings } = useSite();
  return (
    <a href={settings.community.url} target="_blank" rel="noopener noreferrer" className="block bg-bt-primary text-bt-bg" data-testid="home-community-bar">
      <div className="container-bt flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
        <span className="flex items-center gap-3 text-base font-medium"><Users size={18} className="shrink-0" /> {s.text}</span>
        <span className="inline-flex min-h-[44px] shrink-0 items-center justify-center gap-2 self-start rounded-full bg-bt-bg px-5 text-sm font-semibold text-bt-primary sm:self-auto">{s.cta} <ArrowUpRight size={16} /></span>
      </div>
    </a>
  );
}

const WeeklySection = ({ s }) => (
  <section className="container-bt py-12" data-testid="home-weekly-message">
    <WeeklyCard headline={s.headline} ctaLabel={s.cta_label} />
  </section>
);
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

function Hero({ s }) {
  return (
    <section className="relative overflow-hidden" data-testid="home-hero">
      <InfinityMark className="pointer-events-none absolute -left-40 top-10 h-[420px] w-[840px] text-bt-sagedeep opacity-[0.12]" />
      <div className="container-bt relative grid items-center gap-12 py-14 sm:py-20 lg:grid-cols-[1.1fr_0.9fr] lg:py-28">
        <div className="fade-up">
          {s.eyebrow && <p className="eyebrow mb-5">{s.eyebrow}</p>}
          <h1 className="text-4xl leading-[1.05] sm:text-5xl lg:text-6xl" data-testid="hero-headline">{s.headline}</h1>
          <p className="mt-6 font-heading text-xl italic text-bt-primary sm:text-2xl" data-testid="hero-subheadline">{s.subheadline}</p>
          <p className="mt-5 max-w-xl text-base leading-relaxed sm:text-lg">{s.body}</p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row">
            {s.primary_label && <SmartLink to={s.primary_url} className="btn-primary" data-testid="hero-primary-cta">{s.primary_label} <ArrowRight size={18} /></SmartLink>}
            {s.secondary_label && <SmartLink to={s.secondary_url} className="btn-secondary" data-testid="hero-secondary-cta">{s.secondary_label}</SmartLink>}
          </div>
        </div>
        {s.image && (
          <div className="fade-up relative mx-auto w-full max-w-md lg:max-w-none" style={{ animationDelay: "150ms" }}>
            <div className="absolute -inset-4 rounded-[48%_52%_45%_55%/55%_45%_55%_45%] bg-bt-sage" />
            <img src={mediaUrl(s.image)} alt="" className="relative aspect-[4/5] w-full rounded-[44%_56%_48%_52%/52%_48%_52%_48%] object-cover" />
          </div>
        )}
      </div>
    </section>
  );
}

function GrowthChoices({ s }) {
  const { settings } = useSite();
  return (
    <section className="container-bt py-16" data-testid="growth-choices">
      <SectionHead title={s.headline} sub={s.subheadline} />
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {settings.growth_choices.map((c, i) => (
          <SmartLink key={i} to={c.url} data-testid={`growth-choice-${i}`}
            className="group flex min-h-[84px] items-center justify-between gap-4 rounded-[24px] border border-bt-border bg-bt-surface px-6 py-5 transition-[transform,border-color,background-color] duration-300 hover:-translate-y-0.5 hover:border-bt-primary hover:bg-bt-sage">
            <div>
              <p className="font-heading text-lg leading-snug text-bt-heading sm:text-xl">{c.label}</p>
              {c.description && <p className="mt-1 text-sm text-bt-sagedeep">{c.description}</p>}
            </div>
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-bt-sage text-bt-primary transition-colors group-hover:bg-bt-primary group-hover:text-bt-bg"><ArrowRight size={18} /></span>
          </SmartLink>
        ))}
      </div>
    </section>
  );
}

const QuoteBlock = ({ s }) => (
  <section className="container-bt py-12" data-testid="home-quote">
    <div className="relative mx-auto max-w-4xl py-10 text-center">
      <InfinityMark className="mx-auto mb-6 h-10 w-20 text-bt-sagedeep" />
      <p className="font-heading text-3xl leading-snug text-bt-heading sm:text-5xl">{s.text}</p>
      {s.attribution && <p className="eyebrow mt-6">{s.attribution}</p>}
    </div>
  </section>
);

function FreeHubPreview({ s }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/resources", { params: { access: "free", featured: true, limit: 3 } }).then((r) => setItems(r.data));
  }, []);
  return (
    <section className="container-bt py-16" data-testid="home-free-hub">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <SectionHead eyebrow="Free" title={s.headline} sub={s.subheadline} />
        {s.cta_label && <SmartLink to={s.cta_url} className="btn-ghost mb-10 self-start sm:self-auto" data-testid="home-free-hub-cta">{s.cta_label} <ArrowRight size={16} /></SmartLink>}
      </div>
      <ResourceGrid items={items} testid="home-free-grid" />
    </section>
  );
}

function LibraryAreas({ s }) {
  const { settings } = useSite();
  return (
    <section className="container-bt py-16" data-testid="home-library-areas">
      <SectionHead title={s.headline} sub={s.subheadline} />
      <div className="grid gap-4 sm:grid-cols-2">
        {settings.library_areas.map((a) => (
          <SmartLink key={a.slug} to={`/library?area=${a.slug}`} data-testid={`area-card-${a.slug}`}
            className="group relative flex min-h-[220px] flex-col justify-end overflow-hidden rounded-[32px] bg-bt-primary p-7 text-bt-bg">
            {a.image && <img src={mediaUrl(a.image)} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-55 transition-transform duration-700 group-hover:scale-105" />}
            <div className="absolute inset-0" style={{ background: "linear-gradient(0deg, var(--bt-primary) 10%, transparent 90%)" }} />
            <div className="relative">
              <h3 className="text-3xl" style={{ color: "var(--bt-background)" }}>{a.title}</h3>
              <p className="mt-2 max-w-md opacity-90">{a.description}</p>
            </div>
          </SmartLink>
        ))}
      </div>
    </section>
  );
}

function Testimonials({ s }) {
  const { settings } = useSite();
  if (!settings.testimonials?.length) return null;
  return (
    <section className="container-bt py-16" data-testid="home-testimonials">
      <SectionHead title={s.headline} />
      <div className="grid gap-5 md:grid-cols-3">
        {settings.testimonials.map((t, i) => (
          <figure key={i} className="card-bt p-7" data-testid={`testimonial-${i}`}>
            <Quote className="mb-4 text-bt-sagedeep" size={26} />
            <blockquote className="font-heading text-xl leading-snug text-bt-heading">{t.quote}</blockquote>
            <figcaption className="mt-5 text-sm"><span className="font-semibold text-bt-heading">{t.name}</span>{t.role && <span className="text-bt-sagedeep"> · {t.role}</span>}</figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

function MembershipTeaser({ s }) {
  const { settings } = useSite();
  const m = settings.membership;
  return (
    <section className="container-bt py-16" data-testid="home-membership-teaser">
      <div className="flex flex-col gap-6 rounded-[32px] border border-bt-border bg-bt-surface p-8 sm:p-12 lg:flex-row lg:items-center lg:justify-between">
        <div className="max-w-2xl">
          <p className="eyebrow mb-3">{m.name}</p>
          <h2 className="text-3xl sm:text-4xl">{s.headline}</h2>
          <p className="mt-3 text-base md:text-lg">{s.body}</p>
        </div>
        <div className="flex flex-col items-start gap-2 lg:items-end">
          <p className="font-heading text-3xl text-bt-heading">{formatPrice(m)}<span className="text-base text-bt-sagedeep"> / {m.interval}</span></p>
          <SmartLink to={s.cta_url} className="btn-secondary" data-testid="home-membership-cta">{s.cta_label}</SmartLink>
        </div>
      </div>
    </section>
  );
}

function Faq({ s }) {
  const { settings } = useSite();
  if (!settings.faqs?.length) return null;
  return (
    <section className="container-bt py-16" data-testid="home-faq">
      <div className="mx-auto max-w-3xl">
        <SectionHead title={s.headline} />
        <Accordion type="single" collapsible>
          {settings.faqs.map((f, i) => (
            <AccordionItem key={i} value={`f${i}`} className="border-bt-border">
              <AccordionTrigger className="py-6 text-left font-heading text-lg text-bt-heading hover:no-underline" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
              <AccordionContent className="text-base text-bt-text">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

const Custom = ({ s }) => (
  <section className="container-bt py-16" data-testid={`home-custom-${s.id}`}>
    <div className="grid items-center gap-10 lg:grid-cols-2">
      <div>
        {s.eyebrow && <p className="eyebrow mb-3">{s.eyebrow}</p>}
        <h2 className="text-3xl sm:text-4xl">{s.headline}</h2>
        <div className="mt-4"><Prose text={s.body} /></div>
        {s.cta_label && <SmartLink to={s.cta_url} className="btn-primary mt-4">{s.cta_label}</SmartLink>}
      </div>
      {s.image && <img src={mediaUrl(s.image)} alt="" className="w-full rounded-[32px] object-cover" />}
    </div>
  </section>
);

const Wrap = ({ children }) => <div className="container-bt py-12">{children}</div>;

const MAP = {
  email_signup: SignupBar,
  community_bar: CommunityBar,
  weekly_message: WeeklySection,
  hero: Hero, growth_choices: GrowthChoices, quote: QuoteBlock, free_hub: FreeHubPreview, library_areas: LibraryAreas,
  lead_magnet: () => <Wrap><LeadMagnet /></Wrap>, community: () => <Wrap><CommunityPromo /></Wrap>,
  testimonials: Testimonials, membership_teaser: MembershipTeaser, faq: Faq, custom: Custom,
};

export default function Home() {
  const { settings } = useSite();
  const sections = useMemo(() => settings.home.sections.filter((s) => s.enabled), [settings.home.sections]);
  return (
    <div data-testid="home-page">
      {sections.map((s) => {
        const C = MAP[s.type];
        return C ? <C key={s.id} s={s} /> : null;
      })}
    </div>
  );
}
